/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  defaultFilterValues,
  defaultListingPageSize,
  COUNTRY_REGION,
  STATE_PROVINCE,
} from "Constants/AccessControlConstants";
import {
  CREATE_USER,
  CREATE_USER_FAIL,
  CREATE_USER_SUCCESS,
  UPDATE_SELECTED_USER,
  UPDATE_SELECTED_USER_ID,
  UPDATE_USER_LISTING_CURRENT_PAGE,
  UPDATE_USER_LISTING_PAGE_SIZE,
  UPDATE_USER_STATUS,
  UPDATE_USER_STATUS_FAIL,
  UPDATE_USER_STATUS_SUCCESS,
  USER_LOAD_FAIL,
  USER_LOAD_SUCCESS,
  USER_ROLE_CREATE_FAIL,
  USER_ROLE_CREATE_SUCCESS,
  USER_ROLE_DELETE_FAIL,
  USER_ROLE_DELETE_SUCCESS,
  USER_ROLE_UPDATE_FAIL,
  USER_ROLE_UPDATE_SUCCESS,
  USER_ROLES_LOAD,
  USER_ROLES_LOAD_FAIL,
  USER_ROLES_LOAD_SUCCESS,
  USERS_FILTER_COUNTRY_VALUE_REMOVE,
  USERS_FILTER_PROVINCE_VALUE_REMOVE,
  USERS_FILTER_VALUE_ADD,
  USERS_FILTER_VALUE_REMOVE,
  USERS_FILTER_VALUE_UPDATE,
  USERS_RESET_PAGINATION_PROPS,
  USERS_RESET_PROPS,
  USERS_SEARCH,
  USERS_SEARCH_FAIL,
  USERS_SEARCH_SUCCESS,
} from "Constants/ActionTypes";
import { OperationStatus } from "Constants/OperationStatus";

import {
  AccessControlActionTypes,
  IAccessControlFilterValueAdd,
  IAccessControlFilterValueRemove,
  IAccessControlFilterValueUpdate,
  ICreateUser,
  ICreateUserFail,
  ICreateUserRoleFail,
  ICreateUserRoleSuccess,
  ICreateUserSuccess,
  IDeleteUserRoleFail,
  IDeleteUserRoleSuccess,
  ILoadUserFail,
  ILoadUserRolesFail,
  ILoadUserRolesSuccess,
  ILoadUserSuccess,
  IResetProps,
  ISearchUsers,
  ISearchUsersFail,
  ISearchUsersSuccess,
  IUpdateCurrentPage,
  IUpdatePageSize,
  IUpdateSelectedUser,
  IUpdateSelectedUserId,
  IUpdateUserRoleFail,
  IUpdateUserRoleSuccess,
  IUpdateUserStatusFail,
  IUpdateUserStatusSuccess,
} from "./AccessControlAction";
import { IAccessControlState } from "./IAccessControlState";

const INITIAL_STATE: IAccessControlState = {
  tenant: "",
  accessControlFilterValues: defaultFilterValues,
  currentPage: 1,
  filteredCount: 0,
  pageSize: defaultListingPageSize,
  searchText: "",
  selectedUser: {
    emailAddress: "",
    firstName: "",
    lastName: "",
    id: 0,
    dateModified: new Date(),
    countryString: "",
    roleNames: [],
    technologyFocusAreas: [],
    isActive: false,
    status: "",
    region: "",
    role: "",
  },
  selectedUserId: 0,
  newUserId: 0,
  users: [], // check access control container props
  userRoles: [],
  networkOperationStatus: OperationStatus.Idle,
  networkOperationError: "",
};

const applyResetProps = (state: IAccessControlState, action: IResetProps): IAccessControlState => ({
  ...state,
  selectedUser: {
    emailAddress: "",
    firstName: "",
    lastName: "",
    id: 0,
    dateModified: new Date(),
    countryString: "",
    roleNames: [],
    technologyFocusAreas: [],
    isActive: false,
    status: "",
    region: "",
    role: "",
  },
  selectedUserId: 0,
  newUserId: 0,
  userRoles: [],
  networkOperationStatus: OperationStatus.Idle,
});

const applyResetPaginationProps = (): IAccessControlState => INITIAL_STATE;

const applyLoadUser = (
  state: IAccessControlState,
  action: ILoadUserSuccess
): IAccessControlState => ({
  ...state,
  selectedUser: action.user,
  networkOperationStatus: OperationStatus.Idle,
  networkOperationError: "",
});

const applyLoadFailUser = (
  state: IAccessControlState,
  action: ILoadUserFail
): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Failed,
  networkOperationError: action.loadSelectedUserError,
});

const addAccessControlFilterValue = (
  state: IAccessControlState,
  action: IAccessControlFilterValueAdd
): IAccessControlState => ({
  ...state,
  accessControlFilterValues: state.accessControlFilterValues.concat([action.filterValue]),
});

const removeAccessControlFilterValue = (
  state: IAccessControlState,
  action: IAccessControlFilterValueRemove
): IAccessControlState => ({
  ...state,
  accessControlFilterValues: state.accessControlFilterValues.filter(
    (item) =>
      !(item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value)
  ),
});

const removeAccessControlFilterProvinceValue = (
  state: IAccessControlState
): IAccessControlState => ({
  ...state,
  accessControlFilterValues: state.accessControlFilterValues.filter(
    (item) => !(item.fieldName === STATE_PROVINCE)
  ),
});

const removeAccessControlFilterCountryValue = (
  state: IAccessControlState
): IAccessControlState => ({
  ...state,
  accessControlFilterValues: state.accessControlFilterValues.filter(
    (item) => !(item.fieldName === COUNTRY_REGION)
  ),
});

const updateAccessControlFilterValue = (
  state: IAccessControlState,
  action: IAccessControlFilterValueUpdate
): IAccessControlState => ({
  ...state,
  accessControlFilterValues: state.accessControlFilterValues
    .filter((item) => !(item.fieldName === action.filterValue.fieldName))
    .concat([action.filterValue]),
});

const applySearchUsers = (
  state: IAccessControlState,
  action: ISearchUsers
): IAccessControlState => ({
  ...state,
  searchText: action.searchText,
  currentPage: action.searchPage,
  tenant: action.tenant,
  networkOperationStatus: OperationStatus.Pending,
  networkOperationError: "",
});

const applySearchUsersSuccess = (
  state: IAccessControlState,
  action: ISearchUsersSuccess
): IAccessControlState => ({
  ...state,
  users: action.users,
  currentPage: action.searchPage,
  filteredCount: action.filteredCount,
  networkOperationStatus: OperationStatus.Succeeded,
  networkOperationError: "",
});

const applySearchUsersFail = (
  state: IAccessControlState,
  action: ISearchUsersFail
): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Failed,
  networkOperationError: action.error,
});

const applySelectedUserId = (
  state: IAccessControlState,
  action: IUpdateSelectedUserId
): IAccessControlState => ({
  ...state,
  selectedUserId: action.selectedUserId,
  networkOperationStatus: OperationStatus.Pending,
  networkOperationError: "",
});

const applySelectedUser = (
  state: IAccessControlState,
  action: IUpdateSelectedUser
): IAccessControlState => ({
  ...state,
  selectedUser: action.selectedUser,
  networkOperationStatus: OperationStatus.Pending,
  networkOperationError: "",
});

const applyCreateUser = (state: IAccessControlState, action: ICreateUser): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Pending,
  networkOperationError: "",
});

const applyCreateUserSuccess = (
  state: IAccessControlState,
  action: ICreateUserSuccess
): IAccessControlState => ({
  ...state,
  newUserId: action.accessUser.id,
  selectedUser: {
    id: action.accessUser.id,
    role: "",
    emailAddress: action.accessUser.emailAddress,
    firstName: action.accessUser.firstName,
    lastName: "",
    region: "",
    status: "",
    dateModified: new Date(),
    countryString: "",
    roleNames: [],
    technologyFocusAreas: [],
    isActive: false,
  },
  selectedUserId: action.accessUser.id,
  networkOperationStatus: OperationStatus.Idle,
  networkOperationError: "",
  userRoles: [],
});

const applyCreateUserFail = (
  state: IAccessControlState,
  action: ICreateUserFail
): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Failed,
  networkOperationError: action.errorMessage,
});

const applyUpdateUserStatus = (state: IAccessControlState): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Pending,
  networkOperationError: "",
});

const applyUpdateUserStatusSuccess = (
  state: IAccessControlState,
  action: IUpdateUserStatusSuccess
): IAccessControlState => {
  const newState = {
    ...state,
    networkOperationStatus: OperationStatus.Succeeded,
    networkOperationError: "",
  } as IAccessControlState;

  const user = newState.users.find((u) => u.id == action.userStatus.userId);
  if (!!user) user.isActive = action.userStatus.isActive;
  return newState;
};

const applyUpdateUserStatusFail = (
  state: IAccessControlState,
  action: IUpdateUserStatusFail
): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Failed,
  networkOperationError: action.updateUserStatusError,
});

const applyUpdateCurrentPage = (
  state: IAccessControlState,
  action: IUpdateCurrentPage
): IAccessControlState => ({
  ...state,
  currentPage: action.currentPage,
});

const applyUpdatePageSize = (
  state: IAccessControlState,
  action: IUpdatePageSize
): IAccessControlState => ({
  ...state,
  pageSize: action.pageSize,
});

const applyLoadUserRoles = (state: IAccessControlState): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Pending,
  networkOperationError: "",
});

const applyLoadUserRolesSuccess = (
  state: IAccessControlState,
  action: ILoadUserRolesSuccess
): IAccessControlState => ({
  ...state,
  userRoles: action.roles,
  networkOperationStatus: OperationStatus.Succeeded,
  networkOperationError: "",
});

const applyLoadUserRolesFail = (
  state: IAccessControlState,
  action: ILoadUserRolesFail
): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Failed,
  networkOperationError: action.message,
});

const applyDeleteUserRolesSuccess = (
  state: IAccessControlState,
  action: IDeleteUserRoleSuccess
): IAccessControlState => ({
  ...state,
  userRoles: state.userRoles.filter((x) => x.id !== action.userRoleId),
  networkOperationStatus: OperationStatus.Succeeded,
  networkOperationError: "",
});

const applyDeleteUserRolesFail = (
  state: IAccessControlState,
  action: IDeleteUserRoleFail
): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Failed,
  networkOperationError: action.message,
});

const applyCreateUserRolesSuccess = (
  state: IAccessControlState,
  action: ICreateUserRoleSuccess
): IAccessControlState => ({
  ...state,
  userRoles: [...state.userRoles, action.role],
  networkOperationStatus: OperationStatus.Succeeded,
  networkOperationError: "",
});

const applyUpdateUserRolesSuccess = (
  state: IAccessControlState,
  action: IUpdateUserRoleSuccess
): IAccessControlState => ({
  ...state,
  userRoles: [...state.userRoles.filter((ur) => ur.id !== action.role.id), action.role],
  networkOperationStatus: OperationStatus.Succeeded,
  networkOperationError: "",
});

const applyUpdateUserRolesFail = (
  state: IAccessControlState,
  action: IUpdateUserRoleFail
): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Failed,
  networkOperationError: action.message,
});

const applyCreateUserRolesFail = (
  state: IAccessControlState,
  action: ICreateUserRoleFail
): IAccessControlState => ({
  ...state,
  networkOperationStatus: OperationStatus.Failed,
  networkOperationError: action.message,
});

function accessControlReducer(
  state = INITIAL_STATE,
  action: AccessControlActionTypes
): IAccessControlState {
  switch (action.type) {
    case USERS_RESET_PROPS: {
      return applyResetProps(state, action);
    }
    case USERS_RESET_PAGINATION_PROPS: {
      return applyResetPaginationProps();
    }
    case USER_LOAD_SUCCESS: {
      return applyLoadUser(state, action);
    }
    case USER_LOAD_FAIL: {
      return applyLoadFailUser(state, action);
    }
    case USERS_FILTER_VALUE_ADD: {
      return addAccessControlFilterValue(state, action);
    }
    case USERS_FILTER_VALUE_REMOVE: {
      return removeAccessControlFilterValue(state, action);
    }
    case USERS_FILTER_COUNTRY_VALUE_REMOVE: {
      return removeAccessControlFilterCountryValue(state);
    }
    case USERS_FILTER_PROVINCE_VALUE_REMOVE: {
      return removeAccessControlFilterProvinceValue(state);
    }
    case USERS_FILTER_VALUE_UPDATE: {
      return updateAccessControlFilterValue(state, action);
    }
    case USERS_SEARCH: {
      return applySearchUsers(state, action);
    }
    case USERS_SEARCH_FAIL: {
      return applySearchUsersFail(state, action);
    }
    case USERS_SEARCH_SUCCESS: {
      return applySearchUsersSuccess(state, action);
    }
    case UPDATE_SELECTED_USER_ID: {
      return applySelectedUserId(state, action);
    }
    case UPDATE_SELECTED_USER: {
      return applySelectedUser(state, action);
    }
    case CREATE_USER: {
      return applyCreateUser(state, action);
    }
    case CREATE_USER_SUCCESS: {
      return applyCreateUserSuccess(state, action);
    }
    case CREATE_USER_FAIL: {
      return applyCreateUserFail(state, action);
    }
    case UPDATE_USER_STATUS: {
      return applyUpdateUserStatus(state);
    }
    case UPDATE_USER_STATUS_SUCCESS: {
      return applyUpdateUserStatusSuccess(state, action);
    }
    case UPDATE_USER_STATUS_FAIL: {
      return applyUpdateUserStatusFail(state, action);
    }
    case UPDATE_USER_LISTING_CURRENT_PAGE: {
      return applyUpdateCurrentPage(state, action);
    }
    case UPDATE_USER_LISTING_PAGE_SIZE: {
      return applyUpdatePageSize(state, action);
    }
    case USER_ROLES_LOAD: {
      return applyLoadUserRoles(state);
    }
    case USER_ROLES_LOAD_SUCCESS: {
      return applyLoadUserRolesSuccess(state, action);
    }
    case USER_ROLES_LOAD_FAIL: {
      return applyLoadUserRolesFail(state, action);
    }
    case USER_ROLE_DELETE_SUCCESS: {
      return applyDeleteUserRolesSuccess(state, action);
    }
    case USER_ROLE_DELETE_FAIL: {
      return applyDeleteUserRolesFail(state, action);
    }
    case USER_ROLE_CREATE_SUCCESS: {
      return applyCreateUserRolesSuccess(state, action);
    }
    case USER_ROLE_CREATE_FAIL: {
      return applyCreateUserRolesFail(state, action);
    }
    case USER_ROLE_UPDATE_SUCCESS: {
      return applyUpdateUserRolesSuccess(state, action);
    }
    case USER_ROLE_UPDATE_FAIL: {
      return applyUpdateUserRolesFail(state, action);
    }
    default:
      return state;
  }
}

export default accessControlReducer;
